/**
 * Se receber um código embed (<iframe ...>), extrai o atributo src.
 * Se receber só uma URL, apenas faz trim e retorna.
 */
export function normalizeYouTubeUrlOrEmbed(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const value = raw.toString().trim();
  if (!value) return null;

  // Se contiver um iframe, tenta extrair o src
  if (value.includes('<iframe')) {
    const match = value.match(/src=["']([^"']+)["']/i);
    if (match && match[1]) {
      return match[1].trim();
    }
  }

  // Caso contrário, já é uma URL
  return value;
}

/**
 * Extrai o ID do vídeo do YouTube de várias URLs possíveis:
 * - https://www.youtube.com/watch?v=VIDEO_ID
 * - https://youtu.be/VIDEO_ID
 * - https://www.youtube.com/embed/VIDEO_ID
 * - https://www.youtube.com/v/VIDEO_ID
 * - https://www.youtube.com/shorts/VIDEO_ID
 */
export function extractYouTubeId(url: string | null | undefined): string | null {
  if (!url) return null;
  const cleanUrl = url.trim();
  const regex = /(?:youtube(?:-nocookie)?\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=|shorts\/)|youtu\.be\/)([^"&?\/\s]{11})/;
  const match = cleanUrl.match(regex);
  return match ? match[1] : null;
}

/**
 * Verifica se uma URL é do YouTube
 */
export function isYouTubeUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  const cleanUrl = url.trim();
  return /(?:youtube\.com|youtu\.be)/.test(cleanUrl);
}

/**
 * Converte URL do YouTube para formato embed
 * Adiciona parâmetros para melhor integração:
 * - autoplay=1: inicia automaticamente
 * - rel=0: não mostra vídeos relacionados
 * - modestbranding=1: remove logo do YouTube
 */
export function getYouTubeEmbedUrl(url: string | null | undefined): string | null {
  const videoId = extractYouTubeId(url);
  if (!videoId) return null;
  
  return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`;
}

/**
 * Extrai e valida o ID do YouTube de uma URL ou código embed HTML.
 * Esta função é segura contra XSS pois apenas extrai o ID validado.
 * 
 * @param embedOrUrl - Pode ser uma URL do YouTube ou código iframe embed
 * @returns ID validado do YouTube (11 caracteres alfanuméricos) ou null
 */
export function extractAndValidateYouTubeId(embedOrUrl: string | null | undefined): string | null {
  if (!embedOrUrl) return null;
  
  const value = embedOrUrl.trim();
  if (!value) return null;
  
  // Se for um iframe embed, extrair o src primeiro
  let urlToProcess = value;
  if (value.includes('<iframe')) {
    const srcMatch = value.match(/src=["']([^"']+)["']/i);
    if (srcMatch && srcMatch[1]) {
      urlToProcess = srcMatch[1].trim();
    }
  }
  
  // Extrair o ID do YouTube usando a função existente
  const videoId = extractYouTubeId(urlToProcess);
  
  // Validar formato do ID (11 caracteres alfanuméricos, hífens e underscores)
  if (!videoId || !/^[a-zA-Z0-9_-]{11}$/.test(videoId)) {
    return null;
  }
  
  return videoId;
}
